import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { IEpisodeThumbnailInputViewData, IThumbnailError } from 'src/app/core/utils/episode-details-utils';

@Component({
    selector: 'app-episode-thumbnail-input',
    templateUrl: './episode-thumbnail-input.component.html',
    styleUrls: ['./episode-thumbnail-input.component.scss'],
    standalone: false
})
export class EpisodeThumbnailInputComponent implements OnInit {
  @Input() public title: string;

  @Input() public tootipPlacementImageUrl: string;

  @Input() public thumbnail: IEpisodeThumbnailInputViewData | null;

  @Output() public onChange = new EventEmitter<IEpisodeThumbnailInputViewData | null>();

  @ViewChild('episodeThumbnailContainer') public episodeThumbnailContainer: ElementRef<HTMLDivElement>;

  requiredFileType = 'image/png, image/jpg, image/jpeg';

  public isDraggingOverThumbnailElement = false;

  public isProcessingThumbnail = false;

  public showOverlay = false;

  public constructor(
    private readonly matSnackBar: MatSnackBar,
    private readonly domSanitizer: DomSanitizer
  ) {}

  public ngOnInit(): void {}

  /**
   * This method is called by the HTMLInputElement, do not use it directly. Use `setThumbnail` to
   * change the thumbnail instead.
   * @param fileList Accepts a FileList or null
   */
  public onChangeThumbnail(fileList: FileList | null) {
    let before = Promise.resolve<IThumbnailError | null>(null);
    /**
     * If the thumbnail is already set, then we need to unset it.
     */
    if (this.thumbnail !== null) {
      before = this.setThumbnail(null);
    }
    before
      .then((err) => {
        if (err !== null) {
          this.matSnackBar.open(
            `Failed to remove the existing thumbnail: ${err.message}`,
            'Ok'
          );
        }
        return this.setThumbnail(fileList);
      })
      .then((err) => {
        if (err !== null) {
          this.matSnackBar.open(
            `Failed to set the thumbnail: ${err.message}`,
            'Ok'
          );
          return;
        }
        this.onViewDataChange();
      });
  }

  /**
   * Set the value of the component according to the given parameter. The thumbnail will be set to `null`
   * in case something that is not an image is given. HTMLImageElement is used to process the image.
   * @param fileList FileList, Blob or null to unset the thumbnail
   * @returns a promise that will be resolved when the thumbnail is processed or discarded
   */
  public async setThumbnail(
    fileList: File | FileList | null
  ): Promise<IThumbnailError | null> {
    const blob: File | null =
      fileList === null
        ? null
        : 'length' in fileList
        ? fileList[0] ?? null
        : fileList;
    if (this.isProcessingThumbnail) {
      return {
        message:
          'Tried to change thumbnail, while thumbnail processing was ongoing'
      };
    }
    /**
     * If `blob` is null, then the user is trying to unset the thumbnail.
     */
    if (blob === null) {
      const current = this.thumbnail;
      /**
       * If we don't have a thumbnail, then there is nothing to do.
       */
      if (current === null) {
        return null;
      }
      URL.revokeObjectURL(current.url);
      this.thumbnail = null;
      return null;
    }
    this.isProcessingThumbnail = true;
    const blobUrl = URL.createObjectURL(blob);
    const img = new Image();
    return new Promise<IThumbnailError | null>((resolve) => {
      img.addEventListener('load', () => {
        resolve(this.onSuccessfullyProcessedThumbnailImage(img, blobUrl, blob));
      });
      img.addEventListener('error', (e) => {
        resolve(this.onThumbnailImageProcessingError(e, blobUrl, blob));
      });
      img.src = blobUrl;
    });
  }

  private onThumbnailImageProcessingError(
    e: ErrorEvent,
    blobUrl: string,
    _: Blob
  ) {
    // console.error('Failed to process thumbnail image: %o', e);
    // this.matSnackBar.open('Failed to process image', 'Ok');
    URL.revokeObjectURL(blobUrl);
    this.isProcessingThumbnail = false;
    return {
      message: `Failed to process image: ${e.message}`
    };
  }

  private onSuccessfullyProcessedThumbnailImage(
    image: HTMLImageElement,
    blobUrl: string,
    file: File
  ) {
    if (!this.episodeThumbnailContainer) {
      this.isProcessingThumbnail = false;
      return {
        message:
          'Failed to process image. Thumbnail container element was not available.'
      };
    }
    const previewUrl: SafeUrl | string = this.domSanitizer.bypassSecurityTrustUrl(blobUrl);
    const el = this.episodeThumbnailContainer.nativeElement;
    const maxWidth = el.clientWidth;
    const aspect = image.naturalWidth / image.naturalHeight;
    let height = maxWidth / aspect;
    let width = height * aspect;
    /**
     * If the width is greater than the max width, then the image height is used as the limiting factor.
     * This affects how the image is displayed in the UI.
     */
    if (width > maxWidth) {
      height = el.clientHeight;
      width = height * aspect;
    }
    this.thumbnail = {
      width,
      height,
      previewUrl,
      url: blobUrl,
      file
    };
    this.isProcessingThumbnail = false;
    return null;
  }

  public onThumbnailDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverThumbnailElement = true;
    this.onViewDataChange();
  }

  public onThumbnailDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverThumbnailElement = false;
    this.onViewDataChange();
  }

  public onThumbnailDrop(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverThumbnailElement = false;
    this.onChangeThumbnail(event.dataTransfer.files);
  }

  public onThumbnailDragEnd(_: DragEvent) {
    console.log('end');
  }

  private onViewDataChange() {
    this.onChange.emit(this.thumbnail);
  }
}

